// オフスクリーン・サムネイル工房。
//
// デコ・客の「実メッシュ」を小さな透過 PNG にレンダして、UI（パレットのアイコン・
// 図鑑の顔）に使う。絵文字をやめて実モデルと UI を単一ソース化するのが狙い：
// デコや客を足すと、その実メッシュからアイコンが自動で生成される。
//
// ピクセル感を出すため低解像度（既定 48px）でレンダし、img 側の
// image-rendering: pixelated で拡大表示する。1 個の使い捨てレンダラを共有し、
// id ごとに結果（data URL）をキャッシュする。

import * as THREE from 'three';
import { PALETTE } from '../palette';
import { buildDecoMesh, type PlantRender } from './deco_mesh';
import { buildCustomerMesh } from './customer_mesh';
import { getCustomer } from '../sim/customers';
import { isPlantDeco, stageForGrowSec, STAGE_THRESHOLDS_SEC } from '../sim/plants';

/**
 * サムネイルの解像度（正方）。v0.5.1 でドット感を保ちつつ少し上げて（48→64）、
 * 拡大表示を大きくしてもつぶれにくくする。
 */
const THUMB_PX = 64;

/** 遅延生成する共有レンダラ。最初の要求時に作る（無ければ作って使い回す）。 */
let renderer: THREE.WebGLRenderer | null = null;

function getRenderer(): THREE.WebGLRenderer {
  if (renderer) return renderer;
  // preserveDrawingBuffer: render 直後に toDataURL で読み出すため必須。
  // 無いと環境（特に headless）によっては直前のフレームが返り、全サムネが
  // 最初に撮った 1 枚と同じ画像になる。
  const r = new THREE.WebGLRenderer({
    antialias: false,
    alpha: true,
    powerPreference: 'low-power',
    preserveDrawingBuffer: true,
  });
  r.setPixelRatio(1);
  r.setSize(THUMB_PX, THUMB_PX, false);
  r.setClearColor(0x000000, 0); // 透過背景
  renderer = r;
  return r;
}

/** サムネ用の小さなシーン（アイソメ寄りの俯瞰＋パステルを潰さないライト）。 */
function makeThumbScene(): { scene: THREE.Scene; camera: THREE.OrthographicCamera } {
  const scene = new THREE.Scene();
  // ディオラマと同じ向き（右上俯瞰）でそろえる。frustum は snapshot がモデルの
  // バウンディングに合わせて決める（fitCameraToGroup）。初期値は仮。
  const camera = new THREE.OrthographicCamera(-0.6, 0.6, 0.6, -0.6, 0.01, 50);
  camera.position.set(2.2, 2.4, 2.2);
  camera.lookAt(0, 0.22, 0);

  scene.add(new THREE.AmbientLight(0xffffff, 0.95));
  const key = new THREE.DirectionalLight(0xffffff, 0.8);
  key.position.set(2, 4, 2);
  scene.add(key);
  const fill = new THREE.DirectionalLight(new THREE.Color(PALETTE.crystalBlue), 0.3);
  fill.position.set(-2, 1, -1);
  scene.add(fill);
  return { scene, camera };
}

/**
 * モデルのバウンディングボックスに合わせてカメラ（正射影）を寄せ、画角いっぱいに
 * フィットさせる（v0.5.1: サムネのモデルを大きく）。
 *
 * 中心を見るよう lookAt を合わせ、ボックスの対角に少し余白（margin）を持たせた
 * 半幅で正射影の左右上下を決める。これで小さなデコも大きなデコも枠いっぱいに写る。
 */
function fitCameraToGroup(camera: THREE.OrthographicCamera, group: THREE.Object3D): void {
  const box = new THREE.Box3().setFromObject(group);
  if (box.isEmpty()) return;
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  // 俯瞰アイソメで見たときの投影サイズの目安: 水平は xz の広がり、垂直は高さ＋奥行。
  const halfW = Math.max(size.x, size.z) * 0.72;
  const halfH = Math.max(size.y * 0.75 + Math.max(size.x, size.z) * 0.4, halfW);
  const half = Math.max(halfW, halfH) * 1.08; // わずかな余白
  camera.left = -half;
  camera.right = half;
  camera.top = half;
  camera.bottom = -half;
  // 同じ向きを保ったまま中心を見る（カメラ位置はオフセット方向を維持）。
  const dir = new THREE.Vector3(2.2, 2.4, 2.2).normalize();
  camera.position.copy(center.clone().add(dir.multiplyScalar(10)));
  camera.lookAt(center);
  camera.near = 0.01;
  camera.far = 50;
  camera.updateProjectionMatrix();
}

/** Group とその子のジオメトリ／マテリアルを破棄（サムネは撮ったら捨てる）。 */
function disposeGroup(g: THREE.Object3D): void {
  g.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.geometry) m.geometry.dispose();
    const mat = m.material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
    else mat?.dispose();
  });
}

/** 与えた Group を 1 枚レンダして data URL を返す（撮影後に Group は破棄）。 */
function snapshot(group: THREE.Group): string {
  const r = getRenderer();
  const { scene, camera } = makeThumbScene();
  scene.add(group);
  // モデルのバウンディングに合わせてカメラを寄せ、画角いっぱいに大きく写す。
  fitCameraToGroup(camera, group);
  r.render(scene, camera);
  const url = r.domElement.toDataURL('image/png');
  scene.remove(group);
  disposeGroup(group);
  // ライトだけのシーンは GC に任せる（毎回作り直す軽量シーン）。
  return url;
}

// --- デコのサムネイル ---

const decoCache = new Map<string, string>();

/**
 * デコ ID のサムネイル data URL を返す（キャッシュ）。植物デコは「成木」相当の
 * 見栄えする姿で撮る（パレットで木とわかるように）。
 */
export function decoThumbnail(decoId: string): string {
  const cached = decoCache.get(decoId);
  if (cached) return cached;
  let plant: PlantRender | undefined;
  if (isPlantDeco(decoId)) {
    const growSec = STAGE_THRESHOLDS_SEC.mature + 1;
    plant = { stage: stageForGrowSec(growSec), form: { branches: 0.6, bend: 0.4, lush: 0.7, trained: 0.2 } };
  }
  const group = buildDecoMesh(decoId, plant);
  const url = snapshot(group);
  decoCache.set(decoId, url);
  return url;
}

// --- 客のサムネイル（図鑑の顔） ---

const customerCache = new Map<string, string>();

/** 客 ID のサムネイル data URL を返す（キャッシュ）。未知 ID は空文字。 */
export function customerThumbnail(customerId: string): string {
  const cached = customerCache.get(customerId);
  if (cached) return cached;
  const def = getCustomer(customerId);
  if (!def) return '';
  const group = buildCustomerMesh(def.look);
  const url = snapshot(group);
  customerCache.set(customerId, url);
  return url;
}
